<template>
  <div class="container-fluid">
    <page-header :page="page" />
    <filter-manager
      v-model="this.filters"
      :filters="filtersHeader"
      @academicYear="
        updateFiltersData('academicYear', $event, 'onAcademicYearChanged')
      "
      @center="updateFiltersData('center', $event, 'onCenterChanged')"
      @municipality="
        updateFiltersData('municipality', $event, 'onMunicipalityChanged')
      "
      @school="updateFiltersData('school', $event)"
      @subject="updateFiltersData('subject', $event)"
      @grade="updateFiltersData('grade', $event)"
      @readingMedium="updateFiltersData('readingMedium', $event)"
      @search="onSearch"
    />

    <custom-separator
      text="Libros del Estudiante"
      class="my-4 custom-separator-line"
    />

    <CustomResultNotFound
      v-if="showNotResults"
      class="mt-4 row pt-3"
      text="No se ha encontrado resultado de pedidos de libro para este periodo."
    />

    <div v-if="!showNotResults" class="search-result">
      <SearchResultRow
        v-for="book in collections.studentBooks"
        :key="`${book.bookId}-${book.academicYearId}-${book.studentSieId}`"
        ref="book"
        :value="book"
        :collections="collections"
        @refresh-search="onSearch(filters)"
      />
      <pagination
        v-if="pagination.totalItemCount > pagination.perPage"
        :data="pagination"
        :page-size="pagination.perPage"
        @pagination-go-page="onPageChanged"
      />
    </div>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import {
  getAcademicYearsForBooksOrder,
  getStatusList,
  manage,
} from '@/modules/books-requests/api';
import ResourcesService from '@/services/resources.js';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';
import SearchResultRow from './components/SearchResultRow';
import locationService from '@/services/api/location.api.js';
import academicService from '@/services/api/academicUtils.api.js';
import EduEspCode from '@/utils/constants/eduEspCode';

export default {
  name: 'BookManagement',
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    SearchResultRow,
  },
  data() {
    return {
      collections: {
        academicYears: [],
        status: [],
        messages: [],
        centers: [],
        municipalities: [],
        schools: [],
        subjects: [],
        grades: [],
        readingMedium: [],
        studentBooks: [],
      },
      filters: {
        academicYear: null,
        center: null,
        municipality: null,
        school: null,
        subject: null,
        grade: null,
        readingMedium: null,
        searchField: null,
      },
      pagination: {
        perPage: 20,
        currentPage: 1,
        totalItemCount: 0,
        pageCount: 0,
        count: 0,
      },
      centerDisabled: false,
      municipalityDisabled: false,
      schoolDisabled: false,
    };
  },
  methods: {
    updateFiltersData(key, event, callback) {
      if (event != undefined && event != null && event != '') {
        this.filters[key] = event;
      } else {
        this.filters[key] = null;
      }

      if (callback != undefined) {
        if (typeof this[callback] == 'function') {
          this[callback](event);
        }
      }
    },
    async loadAcademicYears() {
      const response = await getAcademicYearsForBooksOrder();
      this.collections.academicYears = response.data;
      this.filters.academicYear = response.data[0];
    },
    async fetchStatus() {
      const response = await getStatusList();
      this.collections.status = response.data;
    },
    async fetchCenters(academicYearId) {
      const { data } = await locationService.getCenters({ academicYearId });

      this.collections.centers = data || [];

      if (data != null && data.length == 1 && data[0].selected) {
        this.filters.center = data[0];
        this.centerDisabled = true;

        this.onCenterChanged(data[0]);
      } else {
        this.centerDisabled = false;
      }
    },
    async fetchMunicipalities(center) {
      const { data } = await locationService.getMunicipalities({
        academicYearId: this.filters.academicYear.id,
        centerId: center.id,
      });

      this.collections.municipalities = data;

      if (data != null && data.length == 1 && data[0].selected) {
        this.filters.municipality = data[0];
        this.municipalityDisabled = true;

        this.onMunicipalityChanged(data[0]);
      } else {
        this.municipalityDisabled = false;
      }
    },
    async fetchSchools(municipality) {
      const { data } = await locationService.getSchools({
        academicYearId: this.filters.academicYear.id,
        municipalityId: municipality.id,
      });

      this.collections.schools = data;

      if (data != null && data.length == 1 && data[0].selected) {
        this.filters.school = data[0];
        this.schoolDisabled = true;
      } else {
        this.schoolDisabled = false;
      }
    },
    async fetchAdvancedFilters() {
      const subjectsResult = await academicService.getEduEsp({
        codetype: EduEspCode.CatlibSubject,
      });
      const readingMediumResult = await academicService.getEduEsp({
        codetype: EduEspCode.CatlibReadingmedium,
      });

      const gradeRange = { first: 3, twelveth: 14 };
      const gradesResult = await academicService.getGrades({
        inferiorLimitId: gradeRange.first,
        superiorLimitId: gradeRange.twelveth,
      });

      this.collections.subjects = subjectsResult.data;
      this.collections.readingMediums = readingMediumResult.data;
      this.collections.grades = gradesResult.data;
    },
    async onAcademicYearChanged(academicYear) {
      this.filters.center = null;
      this.filters.municipality = null;
      this.filters.school = null;

      this.collections.municipalities = [];
      this.collections.schools = [];

      await this.fetchCenters(academicYear.id);
    },
    async onCenterChanged(center) {
      this.filters.municipality = null;
      this.filters.school = null;
      this.collections.municipalities = [];
      this.collections.schools = [];
      if (center) {
        await this.fetchMunicipalities(center);
      }
    },
    async onMunicipalityChanged(municipality) {
      this.filters.school = null;
      this.collections.schools = [];
      if (municipality) {
        await this.fetchSchools(municipality);
      }
    },
    async onSearch(filters, page = 1) {
      const mappedFilter = {
        academicYearId: (filters.academicYear || {}).id,
        statusId: (filters.status || {}).id,
        searchField: filters.searchField,
        centerId: (filters.center || {}).id,
        municipalityId: (filters.municipality || {}).id,
        schoolId: (filters.school || {}).id,
        subjectId: (filters.subject || {}).id,
        gradeId: (filters.grade || {}).id,
        readingMediumId: (filters.readingMedium || {}).id,
        pageIndex: page,
      };

      await manage(mappedFilter).then((res) => {
        this.collections.studentBooks = res.data.data;
        this.pagination = {
          ...this.pagination,
          totalItemCount: res.data.totalItemCount,
          pageCount: res.data.pageCount,
          currentPage: res.data.currentPage,
          count: res.data.count,
        };
      });
    },
    async onPageChanged(currentPage) {
      this.collapseAll();
      this.pagination.currentPage = currentPage;
      await this.onSearch(this.filters, currentPage);
    },
    collapseAll() {
      this.collections.studentBooks.forEach((book, index) => {
        this.$refs.book[index].close();
      });
    },
  },
  computed: {
    page() {
      return {
        icon: 'fas fa-book-open',
        title: 'Manejo de Libros',
        routes: ['Inicio', 'Libros', 'Manejo de Libros'],
      };
    },
    filtersHeader() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.collections.academicYears,
          value: this.filters.academicYear,
          placeholder: 'Seleccione el año',
          fieldName: 'description',
          fieldKey: 'id',
          clearable: false,
          class: 'col-sm-6 col-md-6 col-lg-3 ml-0 mr-0',
          classContainer: 'w-100',
          mapParentValue: this.filters.academicYear == null,
          error: this.collections.messages['R03200049']
            ? this.collections.messages['R03200049'].split(' (')[0]
            : null,
          validations: 'required',
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.collections.status,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-sm-6 col-md-6 col-lg-3 ml-0 mr-0',
          classContainer: 'w-100',
        },
        {
          name: 'searchField',
          component: 'CustomInput',
          placeholder: 'Escriba Nombre o Número de SIE del Estudiante',
          icon: 'fa fa-search orange-icon',
          leftIcon: true,
          class: 'col-12 col-sm-12 col-md-6',
          type: 'text',
        },
        {
          name: 'center',
          label: 'Centro:',
          component: 'CustomDropDown',
          options: this.collections.centers,
          value: this.filters.center,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-4 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.center == null,
          isAdvanced: true,
          disabled: this.centerDisabled,
        },
        {
          name: 'municipality',
          label: 'Municipio:',
          component: 'CustomDropDown',
          options: this.collections.municipalities,
          value: this.filters.municipality,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-4 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.municipality == null,
          isAdvanced: true,
          disabled: this.municipalityDisabled,
        },
        {
          name: 'school',
          label: 'Escuela:',
          component: 'CustomDropDown',
          options: this.collections.schools,
          value: this.filters.school,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6col-md-4 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.school == null,
          isAdvanced: true,
          disabled: this.schoolDisabled,
        },
        {
          name: 'subject',
          label: 'Materia:',
          component: 'CustomDropDown',
          options: this.collections.subjects,
          value: this.filters.subject,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-4 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.subject == null,
          isAdvanced: true,
        },
        {
          name: 'grade',
          label: 'Grado:',
          component: 'CustomDropDown',
          options: this.collections.grades,
          value: this.filters.grade,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-4 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.grade == null,
          isAdvanced: true,
        },
        {
          name: 'readingMedium',
          label: 'Medio de Lectura:',
          component: 'CustomDropDown',
          options: this.collections.readingMediums,
          value: this.filters.readingMedium,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-4 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.readingMedium == null,
          isAdvanced: true,
        },
      ];
    },
    showNotResults() {
      return this.collections.studentBooks.length == 0;
    },
  },
  async mounted() {
    await this.loadAcademicYears();
    await this.fetchStatus();
    await this.fetchCenters(this.filters.academicYear.id);
    await this.fetchAdvancedFilters();
    this.collections.messages = await ResourcesService.LoadValidationMessages([
      'R03200049',
    ]);
  },
};
</script>

<style scoped>
::v-deep .flex-col-3 {
  flex: 1 0 30% !important;
}
</style>
